import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { TokenResponse } from '@wl/ui';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { allowed, type AccessRule, can, hasFeature, isEntitled, isStaff, scopeOf } from './access';
import { api, sessionEvents } from './api';
import type { Me, PermissionScope } from './types';

type Status = 'loading' | 'anonymous' | 'authenticated';

export type LoginResult = { kind: 'ok' } | { kind: 'mfa'; challengeToken: string };

interface AuthContextValue {
  status: Status;
  me: Me | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (challengeToken: string, code: string) => Promise<void>;
  /** Davet kabulü, şifre sıfırlama, handoff gibi token döndüren uçlardan sonra. */
  acceptTokens: (t: TokenResponse) => Promise<void>;
  logout: () => Promise<void>;
  reloadMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const ME_KEY = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [token, setToken] = useState<string | null>(api.getToken());
  const [booted, setBooted] = useState(false);

  useEffect(() => api.onTokenChange(setToken), []);

  useEffect(() => {
    let cancelled = false;
    void api.refresh().finally(() => !cancelled && setBooted(true));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onExpired = () => {
      api.setToken(null);
      qc.clear();
    };
    sessionEvents.addEventListener('expired', onExpired);
    return () => sessionEvents.removeEventListener('expired', onExpired);
  }, [qc]);

  const meQuery = useQuery({
    queryKey: ME_KEY,
    queryFn: () => api.get<Me>('/auth/me'),
    enabled: booted && !!token,
    staleTime: 60_000,
  });

  const acceptTokens = useCallback(
    async (t: TokenResponse) => {
      api.setToken(t.accessToken);
      await qc.fetchQuery({ queryKey: ME_KEY, queryFn: () => api.get<Me>('/auth/me') });
    },
    [qc],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await api.post<TokenResponse | { mfaRequired: true; challengeToken: string }>('/auth/login', { email, password }, { skipRefresh: true });
      if ('mfaRequired' in res) return { kind: 'mfa', challengeToken: res.challengeToken };
      await acceptTokens(res);
      return { kind: 'ok' };
    },
    [acceptTokens],
  );

  const verifyMfa = useCallback(
    async (challengeToken: string, code: string) => {
      await acceptTokens(await api.post<TokenResponse>('/auth/mfa/verify', { challengeToken, code }, { skipRefresh: true }));
    },
    [acceptTokens],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipRefresh: true });
    } finally {
      api.setToken(null);
      qc.clear();
    }
  }, [qc]);

  const reloadMe = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ME_KEY });
  }, [qc]);

  const status: Status = !booted ? 'loading' : !token ? 'anonymous' : meQuery.data ? 'authenticated' : meQuery.isError ? 'anonymous' : 'loading';

  const value = useMemo<AuthContextValue>(
    () => ({ status, me: meQuery.data ?? null, login, verifyMfa, acceptTokens, logout, reloadMe }),
    [status, meQuery.data, login, verifyMfa, acceptTokens, logout, reloadMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('AuthProvider eksik');
  return ctx;
}

/** Oturum açık sayfalarda kullanılır; me her zaman doludur. */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('Oturum yok');
  return me;
}

/** Sayfalarda yetki kontrolü: `const { can } = useAccess(); can('orders.place')`. */
export function useAccess() {
  const me = useMe();
  return useMemo(
    () => ({
      me,
      can: (perm: string | string[], scope?: PermissionScope) => can(me, perm, scope),
      scope: (perm: string | string[]) => scopeOf(me, perm),
      feature: (code: string) => hasFeature(me, code),
      entitled: (code: string) => isEntitled(me, code),
      allowed: (rule: AccessRule) => allowed(me, rule),
      staff: isStaff(me),
      readOnly: !!me.impersonation?.readOnly,
    }),
    [me],
  );
}
